// The owner's LAST per-copy answer, remembered so a cold /collection does not
// have to wait for the capability probe before it starts reading the list.
//
// A HINT, never a capability: it does not promote a pair to per_copy, does
// not unlock a write, and never decides which screen renders (the probe and
// the shared capability store do that, as before). All it changes is that,
// while the probe is in the air for a pair that answered per_copy last time,
// the page sends the per-copy screen's first list read alongside the probe
// instead of after it (one round trip instead of two). The capability rules
// already cover that read: a 200 confirms an unknown pair, a 404 downgrades
// it exactly as the probe's own 404 would.
//
// Where it lives (Codex r1 #4: the account and collection ids are owner
// data, so never web storage):
//  - in memory, for this page, always;
//  - persisted ONLY in the owner's fenced IndexedDB (offline/ownerStorage,
//    key `cb_copies_mode_hint:<uid>`), and only while that owner's records
//    are loaded in this tab: the write goes through the owner's writer
//    context (owner + incarnation, C3 gate), and the C3 owner purge deletes
//    the owner database with it. Without the offline runtime it is memory
//    only (nothing survives a reload).
// Dropped when the pair is downgraded, on sign-out / another account
// (copiesCapability's identity watch), and once older than HINT_TTL_MS (the
// stored record is deleted then, not just ignored).
import { isFencedOwnerKey, ownerStorage } from './offline/ownerStorage'

/** How long a remembered answer is trusted to start a read early. */
export const HINT_TTL_MS = 30 * 24 * 60 * 60_000
/** A pair already remembered is not rewritten more often than this. */
const REWRITE_AFTER_MS = 60 * 60_000

// eslint-disable-next-line i18next/no-literal-string -- storage key prefix (an owner record, see ownerStorage)
const KEY_PREFIX = 'cb_copies_mode_hint:'

interface HintEntry { c: string; at: number }

/** This page's answers, per owner. */
const mem = new Map<string, HintEntry>()

function keyOf(userId: string): string {
  return KEY_PREFIX + userId
}

function parse(raw: string | null): HintEntry | null {
  if (!raw) return null
  try {
    const v = JSON.parse(raw) as Partial<HintEntry> | null
    return v && typeof v.c === 'string' && typeof v.at === 'number' ? { c: v.c, at: v.at } : null
  } catch {
    return null
  }
}

function persist(userId: string, entry: HintEntry | null): void {
  const key = keyOf(userId)
  if (!isFencedOwnerKey(key)) return
  try {
    // Durability is not awaited: a refused or failed commit just means the
    // next cold load waits for the probe, as before.
    const w = ownerStorage.write(key, entry ? JSON.stringify(entry) : null)
    if (w.durable instanceof Promise) w.durable.catch(() => false)
  } catch {
    // Fenced: nothing persisted.
  }
}

function entryOf(userId: string): HintEntry | null {
  const inMem = mem.get(userId)
  if (inMem) return inMem
  const key = keyOf(userId)
  if (!isFencedOwnerKey(key)) return null
  const stored = parse(ownerStorage.getItem(key))
  if (stored) mem.set(userId, stored)
  return stored
}

/** A per-copy answer for (user, collection). */
export function rememberPerCopy(userId: string, collectionId: string, now = Date.now()): void {
  const prev = entryOf(userId)
  if (prev && prev.c === collectionId && now - prev.at >= 0 && now - prev.at < REWRITE_AFTER_MS) return
  const entry = { c: collectionId, at: now }
  mem.set(userId, entry)
  persist(userId, entry)
}

/** The pair was downgraded (or is gone): stop starting reads early for it. */
export function forgetPerCopy(userId: string, collectionId?: string): void {
  const prev = entryOf(userId)
  if (!prev || (collectionId !== undefined && prev.c !== collectionId)) return
  mem.delete(userId)
  persist(userId, null)
}

/** Keep only `owner`'s answer in memory (another account's is in its own fenced database, unreadable here). */
export function keepOnlyOwnerHint(owner: string | null): void {
  for (const uid of [...mem.keys()]) if (uid !== owner) mem.delete(uid)
}

/** True when (user, collection) answered per_copy within HINT_TTL_MS. An expired answer is deleted. */
export function hasPerCopyHint(userId: string | null, collectionId: string | null, now = Date.now()): boolean {
  if (!userId || !collectionId) return false
  const e = entryOf(userId)
  if (!e) return false
  if (now - e.at < 0 || now - e.at >= HINT_TTL_MS) {
    mem.delete(userId)
    persist(userId, null)
    return false
  }
  return e.c === collectionId
}

/** Test seam (memory only; the owner database belongs to its own harness). */
export function __resetCopiesModeHints(): void {
  mem.clear()
}
